import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { getTestProps } from '../../util/testProps';
import { MemberExtractionDocumentRow } from './MemberExtractionDocumentRow';
import { useMemberExtractionDocuments } from './useMemberExtractionDocuments';

export { useMemberExtractionReviewCounts } from './useMemberExtractionReviewCounts';
export type {
  MemberExtractionReviewCounts,
  MemberExtractionReviewCountsResult,
} from './useMemberExtractionReviewCounts';
export { useMemberExtractionDocuments } from './useMemberExtractionDocuments';
export type {
  MemberExtractionDocumentGroup,
  MemberExtractionDocumentsResult,
} from './useMemberExtractionDocuments';

const messages = defineMessages({
  loading: {
    id: 'phc-web-toolkit.memberExtractions.loading',
    defaultMessage: 'Loading extractions',
  },
  empty: {
    id: 'phc-web-toolkit.memberExtractions.empty',
    defaultMessage: 'No extractions',
  },
  error: {
    id: 'phc-web-toolkit.memberExtractions.error',
    defaultMessage: 'Unable to load extractions',
  },
});

const pageStyle = {
  flex: 1,
  minHeight: 0,
  height: '100%',
  paddingRight: 11,
  backgroundColor: '#fff',
};

const pageClassName = 'member-extraction-page';

const listStyle = {
  margin: 0,
  paddingLeft: 20,
  listStyle: 'none',
};

type MemberExtractionsPageProps = {
  patientId: string;
};

export const MemberExtractionsPage: React.FC<MemberExtractionsPageProps> = ({
  patientId,
}) => {
  const { formatMessage } = useIntl();
  const { groups, isLoading, error } = useMemberExtractionDocuments(patientId);

  let body: React.ReactNode;
  if (isLoading) {
    body = <Text size="subbody">{formatMessage(messages.loading)}</Text>;
  } else if (error) {
    body = <Text size="subbody">{formatMessage(messages.error)}</Text>;
  } else if (groups.length === 0) {
    body = <Text size="subbody">{formatMessage(messages.empty)}</Text>;
  } else {
    body = (
      <ul style={listStyle} {...getTestProps('member-extraction-documents')}>
        {groups.map(group => (
          <MemberExtractionDocumentRow
            key={group.sourceReference || group.title}
            patientId={patientId}
            group={group}
          />
        ))}
      </ul>
    );
  }

  return (
    <SimpleBar
      className={pageClassName}
      style={pageStyle}
      autoHide={false}
      forceVisible="y"
      {...getTestProps('member-extractions-page')}
    >
      <style>
        {`.${pageClassName} .simplebar-track.simplebar-vertical{background-color:#cbcbcb;}.${pageClassName} .simplebar-scrollbar:before{background:#999;border-radius:0;opacity:1;left:0;right:0;top:0;bottom:0;}`}
      </style>
      {body}
    </SimpleBar>
  );
};
